/**
 * Tools: what the bot can do, and every word it says about doing it.
 *
 * The biggest screen in the dashboard, because a tool is where the four things that must agree
 * meet: the fields the bot collects, the questions it asks for them, the messages it hands NAB for
 * every outcome, and the placeholders inside those messages. Splitting them across four screens
 * would mean editing a tool in four places and discovering the disagreement at publish time.
 *
 * ## The outcomes grid is the point
 *
 * Rows are outcomes, columns are languages. That shape makes the two mistakes visible without
 * looking for them: an empty cell is a customer who gets English (or nothing), and a placeholder
 * nobody declared is red as you type it. Both are publish-blocking errors, and both are much
 * cheaper to see here than in a validation report.
 *
 * ## Why the preview is live and server-rendered
 *
 * `POST /templates/preview` fills a template with the placeholders' example values. Doing it in the
 * browser would mean a second implementation of placeholder substitution, and the two would
 * disagree about exactly the edge case — a malformed `{{name}`, a name with a digit in it — that
 * the editor is trying to get right.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  StatusPill,
  Table,
  Tabs,
  Textarea,
  MAX_TOOL_DETAILS,
  deleteTool,
  directionOfText,
  importTools,
  previewTemplate,
  saveTool,
  validateTool,
  useToast,
  type BotConfig,
  type BotTool,
  type Column,
  type Requirement,
  type ToolsFileResult,
  type ValidationFinding,
} from "@nab/shared";

import { tagsOf, useBot } from "../config";
import { VersionBar } from "./VersionBar";

const TIER_TONE: Record<string, "success" | "neutral" | "warning"> = {
  read: "neutral",
  lookup: "neutral",
  transactional: "warning",
};

export function ToolsPage(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, loading, problem, reload, save } = useBot();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  const tools = config?.tools ?? [];
  const tool = tools.find((entry) => entry.action === editing) ?? null;

  const columns: Column<BotTool>[] = [
    {
      key: "action",
      header: t("tools.column.action"),
      render: (row) => (
        <button
          type="button"
          onClick={() => setEditing(row.action)}
          className="text-start font-mono text-body-md text-primary underline-offset-2 hover:underline"
        >
          {row.action}
        </button>
      ),
    },
    {
      key: "tier",
      header: t("tools.column.tier"),
      render: (row) => (
        <StatusPill tone={TIER_TONE[row.tier] ?? "neutral"}>
          {t(`tier.${row.tier}`)}
        </StatusPill>
      ),
    },
    {
      key: "active",
      header: t("tools.column.active"),
      render: (row) => (
        <label className="flex items-center gap-2 text-body-sm">
          <input
            type="checkbox"
            checked={row.active}
            onChange={(event) =>
              save(
                () => saveTool({ ...row, active: event.target.checked }),
                t("tools.saved", { action: row.action }),
              )
            }
          />
          {/* An inactive tool is never named to the model, so this switch is the difference
              between "the bot can do this" and "the bot has never heard of it". */}
          <span className="text-textMuted">
            {row.active ? t("tools.on") : t("tools.off")}
          </span>
        </label>
      ),
    },
    {
      key: "fields",
      header: t("tools.column.fields"),
      render: (row) => (
        <span className="text-body-sm text-textMuted">
          {row.requirements.map((r) => r.field).join(", ") || "—"}
        </span>
      ),
    },
    {
      key: "outcomes",
      header: t("tools.column.outcomes"),
      numeric: true,
      render: (row) => row.outcomes.length,
    },
  ];

  if (problem) return <EmptyState title={t("notReady")} description={problem} />;

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="p-4">
        {tool ? (
          <ToolEditor
            tool={tool}
            onClose={() => setEditing(null)}
            onDelete={async () => {
              const ok = await save(
                () => deleteTool(tool.action),
                t("tools.deleted", { action: tool.action }),
              );
              if (ok) setEditing(null);
            }}
          />
        ) : (
          <>
            <div className="mb-3 flex justify-end gap-2">
              {/* Before "New tool", because for anyone arriving with a prepared file this is the
                  screen's main action and the dialog beside it is the slow way to do the same
                  thing fifteen times. */}
              <Button variant="secondary" size="sm" onClick={() => setLoadingFile(true)}>
                {t("tools.import.open")}
              </Button>
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                {t("tools.new")}
              </Button>
            </div>
            <Table
              caption={t("tools.caption")}
              columns={columns}
              rows={tools}
              rowKey={(row) => row.action}
              empty={
                <EmptyState
                  title={t("tools.emptyTitle")}
                  description={loading ? t("loading") : t("tools.emptyBody")}
                />
              }
            />
          </>
        )}
      </div>

      <ImportTools
        open={loadingFile}
        config={config}
        onClose={() => setLoadingFile(false)}
        onLoaded={reload}
      />

      <NewTool
        open={adding}
        existing={tools.map((row) => row.action)}
        onClose={() => setAdding(false)}
        onCreate={async (action, tier) => {
          const ok = await save(
            () => saveTool(blankTool(action, tier)),
            t("tools.created", { action }),
          );
          setAdding(false);
          // Straight into the editor: a tool created from this dialog has a key, a tier and its
          // outcomes, and nothing else. Leaving the operator on the list would mean finding the
          // row they just made to start the work the dialog cannot ask for.
          if (ok) setEditing(action);
        }}
      />
    </div>
  );
}

/**
 * The outcomes a tool of each tier must declare, which publish validation checks.
 *
 * Seeded rather than left empty because a tool with no outcomes carries no messages and cannot
 * publish, and an operator who has just created one should not have to discover that from a
 * validation error. The transactional list is the contract's: every one of them is a real thing
 * that happens to a transfer, and the one with no message is the one that will happen.
 */
const OUTCOMES: Record<BotTool["tier"], string[]> = {
  read: ["success", "empty", "error"],
  lookup: ["success", "error"],
  transactional: [
    "confirm",
    "success",
    "cancelled",
    "otp_failed",
    "beneficiary_not_found",
    "beneficiary_ambiguous",
    "insufficient_funds",
    "rejected",
    "error",
  ],
};

/**
 * A new tool, created **inactive**.
 *
 * That is the important default. An active tool is held to the router's rules — a description and
 * examples in every language — and a half-written one would block every publish until it was
 * finished, including publishes of unrelated work somebody else was waiting on. Inactive, it is
 * exempt from those rules, so it can be built over as many sittings as it takes and switched on
 * when it is ready.
 */
function blankTool(action: string, tier: BotTool["tier"]): BotTool {
  return {
    action,
    tier,
    active: false,
    // A transactional tool must require an OTP: the database refuses it otherwise, and so does
    // publish validation. Set here so the operator never meets either refusal.
    requiresOtp: tier === "transactional",
    routerDescription: "",
    details: "",
    examples: {},
    requirements: [],
    clarifications: {},
    outcomes: OUTCOMES[tier],
    messages: {},
    display: {},
  };
}

/** The two things the editor cannot ask for later, because they key the row and shape it. */
function NewTool({
  open,
  existing,
  onClose,
  onCreate,
}: {
  open: boolean;
  existing: string[];
  onClose: () => void;
  onCreate: (action: string, tier: BotTool["tier"]) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const [action, setAction] = useState("");
  const [tier, setTier] = useState<BotTool["tier"]>("read");

  const clean = action.trim();
  // The same shape the API enforces, checked here so the refusal arrives as you type rather than
  // as a 422. The action key reaches the router prompt and the response's `triggerType`, which is
  // why it is an identifier rather than free text.
  const malformed = clean.length > 0 && !/^[A-Za-z][A-Za-z0-9_]*$/.test(clean);
  const taken = existing.includes(clean);
  const problem = malformed
    ? t("tools.keyShape")
    : taken
      ? t("tools.keyTaken", { action: clean })
      : "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("tools.newTitle")}
      closeLabel={common("actions.close")}
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t("tools.key")}
          hint={t("tools.keyHint")}
          className="font-mono"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          {...(problem ? { error: problem } : {})}
        />
        <Select
          label={t("tools.tier")}
          hint={t("tools.tierHint")}
          value={tier}
          onChange={(event) => setTier(event.target.value as BotTool["tier"])}
          options={[
            { value: "read", label: t("tier.read") },
            { value: "lookup", label: t("tier.lookup") },
            { value: "transactional", label: t("tier.transactional") },
          ]}
        />
        <p className="max-w-prose text-body-sm text-textMuted">{t("tools.newExplain")}</p>
        <div className="flex gap-2">
          <Button
            variant="primary"
            disabled={clean.length === 0 || malformed || taken}
            onClick={() => {
              onCreate(clean, tier);
              setAction("");
              setTier("read");
            }}
          >
            {t("tools.create")}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {common("actions.cancel")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Loading a whole file of tools, which is how a handover actually arrives.
 *
 * The editor below is the right screen for changing a sentence and the wrong one for entering
 * fifteen tools: each needs a key, a tier, its fields, its outcomes and a message per outcome per
 * language, and typing all of that into a browser is slow and is where the mistakes nobody notices
 * come from. So NAB prepares the file — exported from here, or built from another system — and
 * loads it in one go. `docs/m2/tools-file.md` is the format.
 *
 * ## It checks before it loads, always
 *
 * Picking a file runs a dry run: the server does the whole job and writes nothing, and this shows
 * what would change. Then, and only then, is there a button that writes. Two reasons it is not one
 * click: `replace` mode deletes tools, and a bulk edit nobody previewed is a bulk edit nobody can
 * check. The preview is the feature, not a confirmation step.
 *
 * ## The file is parsed here only to fail early
 *
 * `JSON.parse` runs in the browser so that "this is not JSON" arrives with the file name attached
 * instead of as a 422. Everything that matters — the shape, the tiers, the ordering, the publish
 * rules — is the server's, because those rules are the ones that block publishing and a second
 * copy here would be the one that is wrong.
 */
function ImportTools({
  open,
  config,
  onClose,
  onLoaded,
}: {
  open: boolean;
  config: BotConfig | null;
  onClose: () => void;
  onLoaded: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation("app");
  const { t: common } = useTranslation();
  const toast = useToast();

  const [name, setName] = useState("");
  const [document, setDocument] = useState<unknown>(null);
  const [unreadable, setUnreadable] = useState("");
  const [mode, setMode] = useState<"merge" | "replace">("merge");
  const [result, setResult] = useState<ToolsFileResult | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = useCallback(() => {
    setName("");
    setDocument(null);
    setUnreadable("");
    setMode("merge");
    setResult(null);
  }, []);

  const read = async (file: File | null): Promise<void> => {
    // Every pick starts over: a result computed from the previous file, still on screen under a
    // new file name, is the worst thing this dialog could show.
    reset();
    if (!file) return;
    setName(file.name);
    try {
      setDocument(JSON.parse(await file.text()));
    } catch (caught) {
      setUnreadable(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const run = async (apply: boolean): Promise<void> => {
    if (document === null) return;
    setBusy(true);
    try {
      const answer = await importTools(document, { mode, apply });
      setResult(answer);
      if (answer.applied) {
        await onLoaded();
        toast.show({
          tone: "success",
          message: t("tools.import.loaded", {
            count: answer.creates.length + answer.replaces.length,
            version: answer.version ?? "",
          }),
          ...(answer.errors.length > 0
            ? { detail: t("tools.import.thenFix", { count: answer.errors.length }) }
            : {}),
        });
      }
    } catch (caught) {
      toast.show({
        tone: "error",
        message: t("tools.import.failed"),
        ...(caught instanceof ApiError && typeof caught.detail === "string"
          ? { detail: caught.detail }
          : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const refused = result !== null && result.blocking.length > 0;
  const changes = result
    ? result.creates.length + result.replaces.length + result.deletes.length
    : 0;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={t("tools.import.title")}
      closeLabel={common("actions.close")}
    >
      <div className="flex flex-col gap-4">
        <p className="max-w-prose text-body-sm text-textSecondary">
          {t("tools.import.explain")}
        </p>

        <label className="flex flex-col gap-1 text-label-md text-textPrimary">
          {t("tools.import.file")}
          <input
            type="file"
            accept=".json,application/json"
            className="rounded-sm border border-border bg-surfaceRaised p-2 text-body-sm"
            onChange={(event) => void read(event.target.files?.[0] ?? null)}
          />
        </label>

        {unreadable ? (
          <p className="text-body-sm text-danger">
            {t("tools.import.unreadable", { name, detail: unreadable })}
          </p>
        ) : null}

        <Select
          label={t("tools.import.mode")}
          hint={t(`tools.import.${mode}Hint`)}
          value={mode}
          onChange={(event) => {
            setMode(event.target.value as "merge" | "replace");
            // The old result described the other mode, and in one direction that difference is
            // "four tools are deleted".
            setResult(null);
          }}
          options={[
            { value: "merge", label: t("tools.import.merge") },
            { value: "replace", label: t("tools.import.replace") },
          ]}
        />

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={busy && result === null}
            disabled={document === null || busy}
            onClick={() => void run(false)}
          >
            {t("tools.import.check")}
          </Button>
          <Button
            variant="primary"
            loading={busy && result !== null}
            disabled={result === null || refused || changes === 0 || busy}
            onClick={() => void run(true)}
          >
            {t("tools.import.apply", { count: changes })}
          </Button>
          {config ? (
            <Button variant="secondary" onClick={() => downloadTools(config)}>
              {t("tools.import.template")}
            </Button>
          ) : null}
        </div>

        {result ? <ImportResult result={result} /> : null}
      </div>
    </Modal>
  );
}

/** What the dry run found, or what the load did. */
function ImportResult({ result }: { result: ToolsFileResult }): JSX.Element {
  const { t } = useTranslation("app");

  const counts: [string, string[]][] = [
    ["creates", result.creates],
    ["replaces", result.replaces],
    ["deletes", result.deletes],
    ["unchanged", result.unchanged],
  ];
  const nothing =
    result.blocking.length === 0 &&
    result.creates.length + result.replaces.length + result.deletes.length === 0;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <p className="text-body-sm font-medium">
          {result.blocking.length > 0
            ? t("tools.import.refused")
            : result.applied
              ? t("tools.import.done", { version: result.version ?? "" })
              : nothing
                ? t("tools.import.nothing")
                : t("tools.import.would", { version: result.version ?? "" })}
        </p>

        {/* Named, not counted. "4 deleted" is a number somebody accepts; "triggerTransaction,
            getBalance, …" is a list somebody reads. */}
        <dl className="grid gap-x-3 gap-y-1 text-body-sm sm:grid-cols-[auto,1fr]">
          {counts
            .filter(([, actions]) => actions.length > 0)
            .map(([kind, actions]) => (
              <div key={kind} className="contents">
                <dt className="text-textMuted">
                  {t(`tools.import.${kind}`, { count: actions.length })}
                </dt>
                <dd className="font-mono text-body-xs">{actions.join(", ")}</dd>
              </div>
            ))}
        </dl>

        <ImportFindings level="blocking" findings={result.blocking} />
        <ImportFindings level="error" findings={result.errors} />
        <ImportFindings level="warning" findings={result.warnings} />
      </div>
    </Card>
  );
}

/**
 * One list of findings, in the server's own words.
 *
 * `blocking` is shown apart from `error` because they are different situations and collapsing them
 * would hide the one that matters: a blocking finding means nothing was written and the file needs
 * fixing, an error means the tools loaded and Publish will refuse until somebody finishes them.
 */
function ImportFindings({
  level,
  findings,
}: {
  level: "blocking" | "error" | "warning";
  findings: ValidationFinding[];
}): JSX.Element | null {
  const { t } = useTranslation("app");
  if (findings.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-label-md text-textMuted">
        {t(`tools.import.${level}Heading`, { count: findings.length })}
      </p>
      <ul className="flex flex-col gap-2">
        {findings.map((finding) => (
          <li key={`${level}:${finding.where}:${finding.message}`} className="flex gap-2">
            <StatusPill tone={level === "warning" ? "warning" : "danger"}>
              {t(`tools.level.${level === "warning" ? "warning" : "error"}`)}
            </StatusPill>
            <div className="min-w-0">
              <p className="text-body-sm">{finding.message}</p>
              <p className="font-mono text-body-xs text-textMuted">{finding.where}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The current tools and placeholders as a file.
 *
 * This is how anyone gets a template: the format is the snapshot's format, so what comes out here
 * goes straight back in. Built from the config already loaded rather than from a new endpoint —
 * `GET /config` has returned exactly this document since the first screen.
 */
function downloadTools(config: BotConfig): void {
  const file = JSON.stringify(
    { tools: config.tools, placeholders: config.placeholders },
    null,
    2,
  );
  const url = URL.createObjectURL(new Blob([file], { type: "application/json" }));
  const link = window.document.createElement("a");
  link.href = url;
  link.download = `nab-bot-tools-${config.version.label}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------------------

type TabId = "general" | "fields" | "examples" | "messages" | "display";

/**
 * Which tab can fix a finding, from the dotted path the validator addresses it to.
 *
 * `Finding.where` was built for this — `tools.triggerTransaction.messages.success.ly-latn` names
 * the cell. The segment after the action is the tab, with two that do not match their screen:
 * `clarifications` are edited beside the field they belong to, and `outcomes` are the rows of the
 * messages grid.
 *
 * Anything unrecognised lands on General rather than being dropped. A finding nobody can see is
 * worse than one on the wrong tab, and the publish will block on it either way.
 */
const TAB_OF: Record<string, TabId> = {
  tier: "general",
  active: "general",
  requiresOtp: "general",
  routerDescription: "general",
  details: "general",
  requirements: "fields",
  clarifications: "fields",
  examples: "examples",
  outcomes: "messages",
  messages: "messages",
  display: "display",
};

function tabOf(where: string): TabId {
  // tools.<action>.<section>…  — the action may itself contain dots in no world, but splitting on
  // the first three segments is what makes that assumption explicit.
  return TAB_OF[where.split(".")[2] ?? ""] ?? "general";
}

/**
 * The publish rules, run against the tool being edited, as it is edited.
 *
 * Debounced rather than per-keystroke: the check is a round trip that reads the draft snapshot,
 * and a request per character would queue behind itself and show answers to questions the editor
 * has already moved past. 400 ms is after a word, not after a letter.
 *
 * Failures are swallowed deliberately. This is advice on a screen somebody is typing into; a
 * toast saying the validator is unreachable, once per word, would be worse than the silence. The
 * publish button does the same check and that one is allowed to fail loudly.
 */
function useToolFindings(draft: BotTool): {
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
  checking: boolean;
} {
  const [report, setReport] = useState<{
    errors: ValidationFinding[];
    warnings: ValidationFinding[];
  }>({ errors: [], warnings: [] });
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    const timer = setTimeout(() => {
      validateTool(draft)
        .then((result) => {
          if (cancelled) return;
          setReport({ errors: result.errors, warnings: result.warnings });
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setChecking(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft]);

  return { ...report, checking };
}

function ToolEditor({
  tool,
  onClose,
  onDelete,
}: {
  tool: BotTool;
  onClose: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const { config, save } = useBot();
  const [tab, setTab] = useState<TabId>("general");
  // A working copy, so an editor can change three things and save once. The alternative — a save
  // per keystroke — turns the change log into noise and makes an abandoned edit unabandonable.
  const [draft, setDraft] = useState<BotTool>(tool);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(tool);
    setDirty(false);
  }, [tool]);

  const tags = tagsOf(config);
  const languages = useMemo(
    () => Array.from(new Set(tags.map((tag) => tag.split("-")[0]!))),
    [tags],
  );

  const { errors, warnings, checking } = useToolFindings(draft);
  const counts = useMemo(() => {
    const byTab: Record<string, { errors: number; warnings: number }> = {};
    for (const finding of errors) {
      const id = tabOf(finding.where);
      (byTab[id] ??= { errors: 0, warnings: 0 }).errors += 1;
    }
    for (const finding of warnings) {
      const id = tabOf(finding.where);
      (byTab[id] ??= { errors: 0, warnings: 0 }).warnings += 1;
    }
    return byTab;
  }, [errors, warnings]);

  function change(patch: Partial<BotTool>) {
    setDraft((current) => ({ ...current, ...patch }));
    setDirty(true);
  }

  // A tab's label carries its own count, because the problem is usually not on the tab you are
  // looking at. `Tab.label` is a string, so this is the label rather than a badge beside it —
  // which also means a screen reader announces it as part of the tab name.
  function label(id: TabId, text: string): string {
    const count = counts[id];
    if (!count) return text;
    const total = count.errors + count.warnings;
    return count.errors > 0
      ? t("tools.tabCount", { label: text, count: count.errors })
      : t("tools.tabCountWarn", { label: text, count: total });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onClose}>
          {t("tools.back")}
        </Button>
        <h2 className="font-mono text-heading-sm">{draft.action}</h2>
        <StatusPill tone={TIER_TONE[draft.tier] ?? "neutral"}>
          {t(`tier.${draft.tier}`)}
        </StatusPill>
        <div className="ms-auto flex gap-2">
          <Button variant="danger" size="sm" onClick={onDelete}>
            {t("tools.delete")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            onClick={async () => {
              const ok = await save(
                () => saveTool(draft),
                t("tools.saved", { action: draft.action }),
              );
              if (ok) setDirty(false);
            }}
          >
            {t("tools.save")}
          </Button>
        </div>
      </div>

      <Tabs
        label={t("tools.tabs")}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
        tabs={[
          { id: "general", label: label("general", t("tools.tab.general")) },
          { id: "fields", label: label("fields", t("tools.tab.fields")) },
          { id: "examples", label: label("examples", t("tools.tab.examples")) },
          { id: "messages", label: label("messages", t("tools.tab.messages")) },
          { id: "display", label: label("display", t("tools.tab.display")) },
        ]}
      />

      <Findings
        tab={tab}
        active={draft.active}
        errors={errors}
        warnings={warnings}
        checking={checking}
      />

      {tab === "general" ? <General tool={draft} onChange={change} /> : null}
      {tab === "fields" ? (
        <Fields tool={draft} tags={tags} onChange={change} />
      ) : null}
      {tab === "examples" ? (
        <Examples tool={draft} languages={languages} onChange={change} />
      ) : null}
      {tab === "messages" ? (
        <Messages tool={draft} tags={tags} onChange={change} />
      ) : null}
      {tab === "display" ? <Display tool={draft} tags={tags} onChange={change} /> : null}
    </div>
  );
}

/**
 * What is wrong with this tool, on the tab that can fix it.
 *
 * ## Why it does not block saving
 *
 * A new tool is created inactive precisely so it can be built over several sittings, and an
 * inactive tool is exempt from the router's rules. Refusing to save a half-written one would make
 * the dialog that creates it useless. So this reports and the operator decides — the same split
 * the publish validator makes between errors and warnings, and for the same reason: a validator
 * that blocks on everything gets worked around.
 *
 * ## The line that matters is about switching it on
 *
 * An inactive tool with errors is unfinished; an *active* tool with errors blocks every publish,
 * including publishes of unrelated work somebody else is waiting on. Those are different
 * situations and the panel says which one you are in, because "4 problems" means something very
 * different in each.
 */
function Findings({
  tab,
  active,
  errors,
  warnings,
  checking,
}: {
  tab: TabId;
  active: boolean;
  errors: ValidationFinding[];
  warnings: ValidationFinding[];
  checking: boolean;
}): JSX.Element | null {
  const { t } = useTranslation("app");

  const here = [
    ...errors.filter((f) => tabOf(f.where) === tab).map((f) => ({ ...f, level: "error" })),
    ...warnings
      .filter((f) => tabOf(f.where) === tab)
      .map((f) => ({ ...f, level: "warning" })),
  ];
  const elsewhere = errors.length + warnings.length - here.length;

  if (here.length === 0 && elsewhere === 0) {
    // Silence while the first check is in flight, rather than a green tick that turns red.
    return checking ? null : (
      <p className="text-body-sm text-textMuted">{t("tools.noProblems")}</p>
    );
  }

  return (
    <Card>
      <div className="flex flex-col gap-3">
        {errors.length > 0 ? (
          <p className="text-body-sm font-medium">
            {active
              ? t("tools.blocksPublish", { count: errors.length })
              : t("tools.beforeActivating", { count: errors.length })}
          </p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {here.map((finding) => (
            <li key={`${finding.level}:${finding.where}`} className="flex gap-2">
              <StatusPill tone={finding.level === "error" ? "danger" : "warning"}>
                {t(`tools.level.${finding.level}`)}
              </StatusPill>
              <div className="min-w-0">
                <p className="text-body-sm">{finding.message}</p>
                {/* The path, because the message says what is wrong and the path says where.
                    `messages.success.ly-latn` is the cell; no message can be that specific
                    without repeating the grid in prose. */}
                <p className="font-mono text-body-xs text-textMuted">
                  {finding.where.split(".").slice(2).join(".")}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {elsewhere > 0 ? (
          <p className="text-body-sm text-textMuted">
            {t("tools.onOtherTabs", { count: elsewhere })}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// -- General -----------------------------------------------------------------------------

function General({
  tool,
  onChange,
}: {
  tool: BotTool;
  onChange: (patch: Partial<BotTool>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  return (
    <Card className="flex flex-col gap-4 p-4">
      <Select
        label={t("tools.tier")}
        value={tool.tier}
        hint={t("tools.tierHint")}
        onChange={(event) => {
          const tier = event.target.value as BotTool["tier"];
          // A transactional tool must require an OTP — the database refuses it otherwise, and so
          // does the validator. Setting it here means the editor never sees that refusal.
          onChange({ tier, ...(tier === "transactional" ? { requiresOtp: true } : {}) });
        }}
        options={[
          { value: "read", label: t("tier.read") },
          { value: "lookup", label: t("tier.lookup") },
          { value: "transactional", label: t("tier.transactional") },
        ]}
      />

      {/* Also on the list, where it saves immediately. Here it is part of the working copy like
          everything else on this screen, because this is where the validation panel says what
          stands between the tool and being switched on — and reading that, then going back to the
          list to act on it, is a round trip for no reason. */}
      <label className="flex items-center gap-2 text-body-md">
        <input
          type="checkbox"
          checked={tool.active}
          onChange={(event) => onChange({ active: event.target.checked })}
        />
        {t("tools.active")}
      </label>

      <label className="flex items-center gap-2 text-body-md">
        <input
          type="checkbox"
          checked={tool.requiresOtp ?? false}
          disabled={tool.tier === "transactional"}
          onChange={(event) => onChange({ requiresOtp: event.target.checked })}
        />
        {t("tools.requiresOtp")}
      </label>

      <Textarea
        label={t("tools.routerDescription")}
        hint={t("tools.routerDescriptionHint")}
        rows={2}
        value={tool.routerDescription}
        onChange={(event) => onChange({ routerDescription: event.target.value })}
      />

      {/* Directly under the line above, because the two are one thought and the model reads them
          as one block: what the tool is, then when it is the right one. The counter is here and
          not only in the validation panel — a limit you meet at publish is a limit you meet after
          writing 600 characters you now have to cut. */}
      <Textarea
        label={t("tools.details")}
        hint={t("tools.detailsHint", { max: MAX_TOOL_DETAILS })}
        rows={4}
        value={tool.details ?? ""}
        onChange={(event) => onChange({ details: event.target.value })}
      />
      <p
        className={
          (tool.details?.length ?? 0) > MAX_TOOL_DETAILS
            ? "-mt-2 text-body-sm text-danger"
            : "-mt-2 text-body-sm text-textMuted"
        }
      >
        {/* `chars`, not `count`: i18next reads `count` as a plural selector and would want
            _one/_other keys for a string that is the same either way. */}
        {t("tools.detailsCount", {
          chars: tool.details?.length ?? 0,
          max: MAX_TOOL_DETAILS,
        })}
      </p>
    </Card>
  );
}

// -- Fields ------------------------------------------------------------------------------

const FIELD_TYPES = ["string", "enum", "integer", "money", "date", "boolean"] as const;

function Fields({
  tool,
  tags,
  onChange,
}: {
  tool: BotTool;
  tags: string[];
  onChange: (patch: Partial<BotTool>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");

  function update(index: number, patch: Partial<Requirement>) {
    const requirements = tool.requirements.map((requirement, at) =>
      at === index ? { ...requirement, ...patch } : requirement,
    );
    onChange({ requirements });
  }

  function clarify(field: string, tag: string, text: string) {
    const clarifications = {
      ...(tool.clarifications ?? {}),
      [field]: { ...(tool.clarifications?.[field] ?? {}), [tag]: text },
    };
    onChange({ clarifications });
  }

  return (
    <div className="flex flex-col gap-4">
      {tool.requirements.map((requirement, index) => (
        <Card key={requirement.field} className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              label={t("fields.name")}
              value={requirement.field}
              onChange={(event) => update(index, { field: event.target.value })}
              className="font-mono"
            />
            <Select
              label={t("fields.type")}
              value={requirement.type}
              onChange={(event) =>
                update(index, { type: event.target.value as Requirement["type"] })
              }
              options={FIELD_TYPES.map((value) => ({ value, label: value }))}
            />
            <label className="flex items-center gap-2 pb-2 text-body-md">
              <input
                type="checkbox"
                checked={requirement.required ?? false}
                onChange={(event) => update(index, { required: event.target.checked })}
              />
              {t("fields.required")}
            </label>
            <Input
              label={t("fields.default")}
              value={String(requirement.default ?? "")}
              hint={t("fields.defaultHint")}
              onChange={(event) => update(index, { default: event.target.value })}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                onChange({
                  requirements: tool.requirements.filter((_, at) => at !== index),
                })
              }
            >
              {t("fields.remove")}
            </Button>
          </div>

          {requirement.type === "enum" ? (
            <Input
              label={t("fields.values")}
              hint={t("fields.valuesHint")}
              value={(requirement.values ?? []).join(", ")}
              onChange={(event) =>
                update(index, {
                  values: event.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean),
                })
              }
            />
          ) : null}

          {requirement.type === "money" ? (
            <div className="flex flex-wrap gap-3">
              <Input
                label={t("fields.currency")}
                value={requirement.currency ?? ""}
                onChange={(event) => update(index, { currency: event.target.value })}
              />
              <Input
                label={t("fields.decimals")}
                type="number"
                value={String(requirement.decimals ?? "")}
                hint={t("fields.decimalsHint")}
                onChange={(event) =>
                  update(index, { decimals: Number(event.target.value) })
                }
              />
            </div>
          ) : null}

          {requirement.required ? (
            <fieldset className="flex flex-col gap-2 rounded-md border border-borderSubtle p-3">
              <legend className="px-1 text-label-md text-textMuted">
                {/* A required field with no question is a publish-blocking error: the bot would
                    otherwise have to ask in wording nobody approved. */}
                {t("fields.clarification")}
              </legend>
              {tags.map((tag) => (
                <Input
                  key={tag}
                  label={tag}
                  dir={directionOfText(tool.clarifications?.[requirement.field]?.[tag] ?? "")}
                  value={tool.clarifications?.[requirement.field]?.[tag] ?? ""}
                  onChange={(event) =>
                    clarify(requirement.field, tag, event.target.value)
                  }
                />
              ))}
            </fieldset>
          ) : null}
        </Card>
      ))}

      <Button
        variant="secondary"
        onClick={() =>
          onChange({
            requirements: [
              ...tool.requirements,
              { field: `field${tool.requirements.length + 1}`, type: "string" },
            ],
          })
        }
      >
        {t("fields.add")}
      </Button>
    </div>
  );
}

// -- Examples ----------------------------------------------------------------------------

function Examples({
  tool,
  languages,
  onChange,
}: {
  tool: BotTool;
  languages: string[];
  onChange: (patch: Partial<BotTool>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  return (
    <Card className="flex flex-col gap-4 p-4">
      <p className="max-w-prose text-body-sm text-textMuted">
        {/* These are the few-shots in the router prompt, which is the only tool text the model
            ever sees. Three per language is the floor the publish validation warns below. */}
        {t("examples.hint")}
      </p>
      {languages.map((language) => (
        <Textarea
          key={language}
          label={language}
          rows={5}
          dir={directionOfText((tool.examples[language] ?? []).join("\n"))}
          hint={t("examples.perLine")}
          value={(tool.examples[language] ?? []).join("\n")}
          onChange={(event) =>
            onChange({
              examples: {
                ...tool.examples,
                [language]: event.target.value
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean),
              },
            })
          }
        />
      ))}
    </Card>
  );
}

// -- Outcomes and messages ---------------------------------------------------------------

function Messages({
  tool,
  tags,
  onChange,
}: {
  tool: BotTool;
  tags: string[];
  onChange: (patch: Partial<BotTool>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const { config } = useBot();
  const declared = useMemo(
    () => new Set((config?.placeholders ?? []).map((entry) => entry.name)),
    [config],
  );

  function set(outcome: string, tag: string, text: string) {
    onChange({
      messages: {
        ...tool.messages,
        [outcome]: { ...(tool.messages[outcome] ?? {}), [tag]: text },
      },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-prose text-body-sm text-textMuted">{t("messages.hint")}</p>

      {tool.outcomes.map((outcome) => (
        <Card key={outcome} className="flex flex-col gap-3 p-4">
          <div className="flex items-center gap-2">
            <h3 className="font-mono text-body-md">{outcome}</h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                onChange({
                  outcomes: tool.outcomes.filter((name) => name !== outcome),
                })
              }
            >
              {t("messages.removeOutcome")}
            </Button>
          </div>

          {tags.map((tag) => (
            <MessageCell
              key={tag}
              tag={tag}
              declared={declared}
              value={tool.messages[outcome]?.[tag] ?? ""}
              onChange={(text) => set(outcome, tag, text)}
            />
          ))}
        </Card>
      ))}

      <AddOutcome
        existing={tool.outcomes}
        onAdd={(name) => onChange({ outcomes: [...tool.outcomes, name] })}
      />
    </div>
  );
}

const PLACEHOLDER = /\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/g;

function MessageCell({
  tag,
  value,
  declared,
  onChange,
}: {
  tag: string;
  value: string;
  declared: Set<string>;
  onChange: (text: string) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [filled, setFilled] = useState<string>("");

  // Undeclared names are computed locally *for the red highlight only* — instant feedback as you
  // type. The preview beside it is the server's, which is the one that matches what ships.
  const undeclared = useMemo(() => {
    const names = Array.from(value.matchAll(PLACEHOLDER), (match) => match[1]!);
    return names.filter((name) => !declared.has(name));
  }, [value, declared]);

  const preview = useCallback(async (template: string) => {
    if (!template.trim()) {
      setFilled("");
      return;
    }
    try {
      setFilled((await previewTemplate(template)).filled);
    } catch {
      // A preview is a convenience. Losing it must not stop someone editing a message.
      setFilled("");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void preview(value), 400);
    return () => clearTimeout(timer);
  }, [value, preview]);

  return (
    <div className="flex flex-col gap-1">
      <Textarea
        label={tag}
        rows={2}
        dir={directionOfText(value)}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...(undeclared.length > 0
          ? { error: t("messages.undeclared", { names: undeclared.join(", ") }) }
          : {})}
      />
      {filled ? (
        <p
          dir={directionOfText(filled)}
          className="rounded-sm bg-surfaceSunken px-3 py-2 text-body-sm text-textMuted [unicode-bidi:isolate]"
        >
          {filled}
        </p>
      ) : null}
    </div>
  );
}

function AddOutcome({
  existing,
  onAdd,
}: {
  existing: string[];
  onAdd: (name: string) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [name, setName] = useState("");
  const clean = name.trim();
  const ok = clean.length > 0 && !existing.includes(clean);

  return (
    <div className="flex items-end gap-2">
      <Input
        label={t("messages.addOutcome")}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <Button
        variant="secondary"
        disabled={!ok}
        onClick={() => {
          onAdd(clean);
          setName("");
        }}
      >
        {t("messages.add")}
      </Button>
    </div>
  );
}

// -- Display -----------------------------------------------------------------------------

function Display({
  tool,
  tags,
  onChange,
}: {
  tool: BotTool;
  tags: string[];
  onChange: (patch: Partial<BotTool>) => void;
}): JSX.Element {
  const { t } = useTranslation("app");
  const quick = tool.display.quickReplies;
  const perLanguage = quick && !Array.isArray(quick) ? quick : {};

  return (
    <Card className="flex flex-col gap-4 p-4">
      <Input
        label={t("display.card")}
        hint={t("display.cardHint")}
        value={tool.display.card ?? ""}
        onChange={(event) =>
          onChange({ display: { ...tool.display, card: event.target.value } })
        }
      />
      <fieldset className="flex flex-col gap-2 rounded-md border border-borderSubtle p-3">
        <legend className="px-1 text-label-md text-textMuted">
          {t("display.quickReplies")}
        </legend>
        {tags.map((tag) => (
          <Input
            key={tag}
            label={tag}
            dir={directionOfText((perLanguage[tag] ?? []).join(", "))}
            hint={t("display.quickRepliesHint")}
            value={(perLanguage[tag] ?? []).join(", ")}
            onChange={(event) =>
              onChange({
                display: {
                  ...tool.display,
                  quickReplies: {
                    ...perLanguage,
                    [tag]: event.target.value
                      .split(",")
                      .map((reply) => reply.trim())
                      .filter(Boolean),
                  },
                },
              })
            }
          />
        ))}
      </fieldset>
    </Card>
  );
}
